import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, ExternalLink } from 'lucide-react'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'

// ---------------------------------------------------------------------------
// Personal Access Token connect modal (used when OAuth is not configured)
// ---------------------------------------------------------------------------

function GitLabCredentialsModal({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  onComplete: () => void
}) {
  const [isSaving, setIsSaving] = useState(false)
  const [token, setToken] = useState('')
  const [baseUrl, setBaseUrl] = useState('https://gitlab.com')
  const [groupId, setGroupId] = useState('')

  const handleSave = async () => {
    if (!token.trim()) { toast.error('Personal access token is required'); return }

    try {
      new URL(baseUrl)
    } catch {
      toast.error('Invalid GitLab URL')
      return
    }

    setIsSaving(true)
    try {
      await trpcClient.integrations.createGitLabWithPat.mutate({
        token: token.trim(),
        instanceUrl: baseUrl.trim(),
        groupId: groupId.trim() || undefined,
      })
      toast.success('GitLab connected')

      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onOpenChange(false)
      onComplete()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to connect GitLab')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type="GitLab" />
            Connect GitLab
          </DialogTitle>
          <DialogDescription>
            Connect using a personal access token for compliance scanning and project access.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 py-2 px-6" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="gl-base-url">GitLab URL</Label>
            <Input
              id="gl-base-url"
              name="gitlab-base-url"
              type="url"
              autoComplete="off"
              placeholder="https://gitlab.com"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              Use https://gitlab.com for cloud, or your self-hosted instance URL.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gl-token">Personal access token</Label>
            <Input
              id="gl-token"
              name="gitlab-token"
              type="password"
              autoComplete="new-password"
              placeholder="glpat-..."
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              Create a token in GitLab Settings &gt; Access Tokens with <code className="text-[10px]">api</code> and <code className="text-[10px]">read_repository</code> scopes.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gl-group">Group ID (optional)</Label>
            <Input
              id="gl-group"
              name="gitlab-group"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="12345"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              Limit scanning to a specific group. Leave empty to scan all accessible projects.
            </p>
          </div>

          <a
            href="https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] text-cobalt-strong hover:underline"
          >
            How to create a personal access token
            <ExternalLink className="h-3 w-3" />
          </a>
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Config modal router — picks credentials modal vs manage dialog
// ---------------------------------------------------------------------------

function GitLabConfigModalWrapper(props: ConfigModalProps) {
  const { open, onOpenChange, integration, workspaceId, onComplete } = props

  if (integration) {
    return null
  }

  return (
    <GitLabCredentialsModal
      open={open}
      onOpenChange={onOpenChange}
      workspaceId={workspaceId}
      onComplete={onComplete}
    />
  )
}

// ---------------------------------------------------------------------------
// Provider export
// ---------------------------------------------------------------------------

export const GitLabProvider: IntegrationProvider = {
  id: 'GitLab',
  name: 'GitLab',
  description: 'Connect GitLab (cloud or self-hosted) to sync documents, MRs & Issues',
  Icon: () => <IntegrationIcon type="GitLab" />,
  ConfigModal: GitLabConfigModalWrapper,

  handleConnect: async ({ redirectUrl, onSuccess, onFinish, config }) => {
    const typesData =
      queryClient.getQueryData(trpc.integrations.getAvailableIntegrationTypes.queryKey()) ??
      (await queryClient.fetchQuery(trpc.integrations.getAvailableIntegrationTypes.queryOptions()))
    const authMethod = (typesData as Array<{ id: string; authMethod?: string }>)?.find((t) => t.id === 'GitLab')?.authMethod

    if (authMethod === 'oauth') {
      try {
        const result = await trpcClient.integrations.initGitLabAuth.mutate({
          redirectUrl:
            redirectUrl ||
            `${window.location.origin}/auth-callback/GitLab`,
          providerType: 'GitLab',
          instanceUrl:
            typeof config?.instanceUrl === 'string'
              ? config.instanceUrl
              : 'https://gitlab.com',
        })

        openOAuthConnectPopup({
          authUrl: result.authUrl,
          windowName: 'gitlab-auth',
          provider: 'GitLab',
          onSuccess,
          onError: (message) =>
            toast.error(`GitLab authentication failed: ${message}`),
          onPopupBlocked: () =>
            toast.error(
              'OAuth popup was blocked. Allow popups and try again.'
            ),
          onFinish,
        })
      } catch (error) {
        console.error('Error initiating GitLab OAuth:', error)
        toast.error('Failed to start GitLab OAuth. Please try again.')
        onFinish?.('error')
      }
      return
    }

    onSuccess?.({ provider: 'GitLab' })
  },

  onConnectComplete: async ({ code, state }) => {
    return trpcClient.integrations.completeGitLabAuth.mutate({ code, state })
  },
}
